import { monto } from "@/componentes/tesoreria/dinero"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"

import { type ReporteGeneral, VistaCuentasYResultados } from "./CuentasYResultados"

export type Gerencial = {
  semana: ReporteGeneral
  mes: ReporteGeneral
  movimientos: {
    monedas: { moneda: string; entradas: string; salidas: string; neto: string }[]
    entradas: number
    salidas: number
  }
  ajustes: {
    fecha: string
    que: string
    recurso: string
    codigo: string | null
    motivo: string | null
  }[]
}

/**
 * El resumen gerencial de los viernes (28/10, minuta del 27/10, punto 8), tal como se
 * congeló: la semana, los movimientos de los bancos, los ajustes y cómo va el mes. Lo pinta
 * la foto pública (`/m/<token>`) con los mismos componentes de Administración → Reportes.
 */
export function VistaGerencial({ g }: { g: Gerencial }) {
  return (
    <div className="space-y-8">
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">La semana</h2>
        <VistaCuentasYResultados r={g.semana} />
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Movimientos de los bancos</h2>
        <Tabla etiqueta="Entradas y salidas de la semana por moneda">
          <Cabecera>
            <Th>Moneda</Th>
            <Th className="text-right">Entraron</Th>
            <Th className="text-right">Salieron</Th>
            <Th className="text-right">Neto</Th>
          </Cabecera>
          <Cuerpo>
            {g.movimientos.monedas.length === 0 && (
              <TablaVacia colSpan={4}>Sin movimientos en la semana.</TablaVacia>
            )}
            {g.movimientos.monedas.map((m) => (
              <Fila key={m.moneda}>
                <Td>{m.moneda}</Td>
                <Td className="text-right tabular-nums">{monto(m.entradas)}</Td>
                <Td className="text-right tabular-nums">{monto(m.salidas)}</Td>
                <Td className="text-right tabular-nums">{monto(m.neto)}</Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Ajustes de la semana</h2>
        <Tabla etiqueta="Correcciones de la semana">
          <Cabecera>
            <Th>Cuándo</Th>
            <Th>Qué</Th>
            <Th>Sobre</Th>
            <Th>Motivo</Th>
          </Cabecera>
          <Cuerpo>
            {g.ajustes.length === 0 && (
              <TablaVacia colSpan={4}>No hubo anulaciones ni correcciones.</TablaVacia>
            )}
            {g.ajustes.map((a, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: filas congeladas de la bitácora
              <Fila key={i}>
                <Td className="tabular-nums">{a.fecha}</Td>
                <Td>{a.que}</Td>
                <Td>{a.codigo ?? a.recurso}</Td>
                <Td>{a.motivo ?? "—"}</Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Cómo va el mes</h2>
        <VistaCuentasYResultados r={g.mes} />
      </section>
    </div>
  )
}
